// Разбор публичного адреса: что за страница, на каком языке и про какой курс.
//
// ─────────────────────────────────────────────────────────────────────────────
// Зачем один файл на сервер и клиент
// ─────────────────────────────────────────────────────────────────────────────
// Учёт посещаемости получает адреса из двух источников: серверный хук
// SEO-раздачи и beacon перехода внутри SPA. Если каждая сторона разберёт путь
// по-своему, один и тот же курс попадёт в отчёт двумя разными ключами, и топ
// страниц молча раздвоится. Поэтому классификатор ровно один, и клиент НИКОГДА
// не присылает готовую классификацию — только сырой путь, а разбирает сервер.
//
// Побочная, но не менее важная роль — гейт: `classifyPublicPath` возвращает
// `null` для всего, что не является публичной страницей. Это то, что не даёт
// подсунуть в таблицу публичного трафика адрес вроде `/materials/<id>`.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему список страниц берётся отсюда, а не пишется заново
// ─────────────────────────────────────────────────────────────────────────────
// Перечень публичных адресов уже живёт в двух местах: в боевом Caddyfile (он
// правится руками) и в `STATIC_PAGES` SEO-раздачи. Этот файл — третье, и чтобы
// он не разъехался с остальными, тест сверяет его список со `STATIC_PAGES`.
// Форма языкового адреса не переизобретается — берётся из `seo-locale-urls`.

import { isSupportedLocale } from "../constants.js";
import { CATALOG_MAX_PAGE } from "./catalog-pagination.js";

/**
 * Тип публичной страницы.
 *
 * `sitemap` здесь не ради полноты: частота, с которой роботы дёргают карту
 * курсов, — самостоятельная SEO-метрика, и терять её незачем.
 */
export type PublicPageType = "landing" | "static" | "catalog" | "course" | "sitemap";

/** Статические публичные страницы. Обязаны совпадать со `STATIC_PAGES` SEO-раздачи. */
export const PUBLIC_STATIC_PAGE_KEYS = ["about", "faq", "method", "pricing"] as const;
export type PublicStaticPageKey = (typeof PUBLIC_STATIC_PAGE_KEYS)[number];

export interface PublicPathInfo {
	pageType: PublicPageType;
	/** about | faq | method | pricing — иначе null. */
	pageKey: PublicStaticPageKey | null;
	/** Slug курса для `pageType === "course"`, иначе null. */
	courseSlug: string | null;
	/**
	 * Язык ОТКРЫТОЙ версии страницы из префикса адреса.
	 * `null` = голый путь, то есть английская версия (`BARE_PATH_LOCALE`).
	 */
	locale: string | null;
	/**
	 * Нормализованный путь без языкового префикса и без query — кроме `?page=N`
	 * у каталога. Он и есть ключ, по которому страница узнаётся в отчётах.
	 */
	normalizedPath: string;
	/** Стабильный ключ строки отчёта: slug курса, ключ статической страницы или ''. */
	pageRef: string;
}

const STATIC_KEY_SET = new Set<string>(PUBLIC_STATIC_PAGE_KEYS);

/** Слаг курса: то же, что генерирует витрина. Ограничение длины — против мусора в ключе. */
const COURSE_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,119}$/;

/**
 * Номер страницы каталога из query.
 *
 * Он сохраняется в нормализованном пути намеренно: номерные страницы каталога
 * индексируются отдельно (canonical их содержит), значит это РАЗНЫЕ страницы.
 * Все прочие параметры (фильтры, `?start=1`, utm-метки) отбрасываются — смена
 * фильтра не является новым просмотром страницы.
 */
function readCatalogPage(search: string): number | null {
	if (!search) return null;
	const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("page");
	if (raw === null) return null;
	const page = Number.parseInt(raw, 10);
	if (!Number.isInteger(page) || page < 2 || page > CATALOG_MAX_PAGE) return null;
	return page;
}

/**
 * Разобрать публичный адрес.
 *
 * Принимает как путь (`/de/catalog/python-pro`), так и путь с query. Возвращает
 * `null`, если адрес не относится к публичной зоне, — вызывающая сторона обязана
 * трактовать это как «не учитываем», а не как «неизвестная страница».
 */
export function classifyPublicPath(input: string): PublicPathInfo | null {
	if (typeof input !== "string" || input.length === 0 || input.length > 2048) return null;

	const hashAt = input.indexOf("#");
	const withoutHash = hashAt === -1 ? input : input.slice(0, hashAt);
	const queryAt = withoutHash.indexOf("?");
	const rawPath = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
	const search = queryAt === -1 ? "" : withoutHash.slice(queryAt);

	if (!rawPath.startsWith("/")) return null;

	// Хвостовой слэш незначим везде, кроме корня: `/catalog/` и `/catalog` —
	// одна страница, и разводить их в отчёте нечестно.
	const trimmed = rawPath.length > 1 ? rawPath.replace(/\/+$/, "") : rawPath;
	const segments = trimmed.split("/").filter((s) => s.length > 0);

	// Карта курсов живёт на своём адресе и языкового префикса не имеет.
	if (segments.length === 1 && segments[0] === "sitemap-courses.xml") {
		return {
			pageType: "sitemap",
			pageKey: null,
			courseSlug: null,
			locale: null,
			normalizedPath: "/sitemap-courses.xml",
			pageRef: "",
		};
	}

	// Языковой префикс. Голый путь — английская версия, отдельного `/en/` нет.
	let locale: string | null = null;
	let rest = segments;
	const head = segments[0];
	if (head !== undefined && head.length === 2 && isSupportedLocale(head)) {
		locale = head;
		rest = segments.slice(1);
	}

	if (rest.length === 0) {
		return {
			pageType: "landing",
			pageKey: null,
			courseSlug: null,
			locale,
			normalizedPath: "/",
			pageRef: "",
		};
	}

	const first = rest[0];
	if (first === undefined) return null;

	if (rest.length === 1 && STATIC_KEY_SET.has(first)) {
		return {
			pageType: "static",
			pageKey: first as PublicStaticPageKey,
			courseSlug: null,
			locale,
			normalizedPath: `/${first}`,
			pageRef: first,
		};
	}

	if (first === "catalog") {
		if (rest.length === 1) {
			const page = readCatalogPage(search);
			return {
				pageType: "catalog",
				pageKey: null,
				courseSlug: null,
				locale,
				normalizedPath: page === null ? "/catalog" : `/catalog?page=${page}`,
				pageRef: "",
			};
		}
		// Ровно один сегмент после `/catalog` — курс. Глубже публичных адресов
		// нет: `/catalog/a/b` на проде отдаёт 404 приложения, а не страницу.
		const slug = rest[1];
		if (rest.length === 2 && slug !== undefined && COURSE_SLUG_RE.test(slug)) {
			return {
				pageType: "course",
				pageKey: null,
				courseSlug: slug,
				locale,
				normalizedPath: `/catalog/${slug}`,
				pageRef: slug,
			};
		}
		return null;
	}

	return null;
}

/** Быстрый предикат: относится ли адрес к публичной зоне. */
export function isPublicTrackablePath(input: string): boolean {
	return classifyPublicPath(input) !== null;
}
